<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import { useEditions } from '@/composables/useEditions'
import { useUnsavedChanges } from '@/composables/useUnsavedChanges'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const { t } = useI18n()
const route = useRoute()
const { event, fetchEvent } = useEvents()
const { edition, loading, fetchEdition } = useEditions()
const { isDirty, markClean } = useUnsavedChanges()

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string

const tabs = [
  { name: 'organizer.editions.settings.general', label: () => t('editions.settings.tab_general') },
  { name: 'organizer.editions.settings.cancellation', label: () => t('editions.settings.tab_cancellation') },
  { name: 'organizer.editions.settings.switch-race', label: () => t('editions.settings.tab_switch_race') },
  { name: 'organizer.editions.settings.race-rules', label: () => t('editions.settings.tab_race_rules') },
  { name: 'organizer.editions.settings.terms', label: () => t('editions.settings.tab_terms') },
]

// On narrow screens the tab row scrolls horizontally on its own (see the
// wrapping overflow-x-auto container in the template) instead of pushing the
// whole page into a sideways scroll. Keep the active tab visible whenever the
// route changes, including on initial mount, in case it starts off-screen.
const tabRefs = new Map<string, HTMLElement>()

function setTabRef(name: string, el: Element | ComponentPublicInstance | null): void {
  // RouterLink is a component: the ref resolves to its public instance, whose
  // $el is the rendered <a>. Handle both that and a plain Element defensively.
  const node = el && '$el' in el ? (el as ComponentPublicInstance).$el as unknown : el
  if (node instanceof HTMLElement) {
    tabRefs.set(name, node)
  } else {
    tabRefs.delete(name)
  }
}

function scrollActiveTabIntoView(): void {
  const el = tabRefs.get(route.name as string)
  // jsdom (used by Vitest) does not implement scrollIntoView, guard defensively.
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
}

onMounted(async () => {
  await Promise.all([fetchEvent(eventSlug), fetchEdition(eventSlug, editionSlug)])
  await nextTick()
  scrollActiveTabIntoView()
})

watch(() => route.name, () => void nextTick(scrollActiveTabIntoView))

onUnmounted(() => {
  tabs.forEach((tab) => markClean(tab.name))
})
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}/editions/${editionSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="edition">{{ edition.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">{{ t('editions.settings.heading') }}</span>
      </nav>

      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white">
          {{ event?.name ?? '…' }} · {{ edition?.name ?? '…' }}
        </h1>
        <p class="text-slate-400 mt-1">{{ t('editions.settings.heading') }}</p>
      </div>

      <!-- Loading -->
      <AppSpinner v-if="loading" />

      <template v-else-if="edition">
        <!-- Tab bar -->
        <div class="overflow-x-auto mb-8">
          <div class="border-b border-slate-700">
            <nav class="-mb-px flex gap-6">
              <RouterLink
                v-for="tab in tabs"
                :key="tab.name"
                :ref="(el) => setTabRef(tab.name, el)"
                :to="{ name: tab.name }"
                class="pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 shrink-0"
                :class="route.name === tab.name
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-slate-400 hover:text-white hover:border-slate-500'"
              >
                {{ tab.label() }}
                <span
                  v-if="isDirty(tab.name)"
                  class="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"
                />
              </RouterLink>
            </nav>
          </div>
        </div>

        <!-- Active panel -->
        <RouterView />
      </template>
    </main>
  </OrganizerLayout>
</template>
